// One vehicle type card, used by the Our Fleet page (/fleet) and by the Fleet
// Line-up section on the home page.
//
// Layout
//   Mobile (below md):  picture + name on top, a thin divider, then the two
//                       capacity figures side by side.
//   Tablet/desktop:     picture + name on the left, the two capacity figures
//                       stacked on the right.
//
// Must be rendered inside a <Stagger> (see components/motion): the card fades
// in with its siblings, lifts on hover, and the picture zooms on the same hover.

import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { PassengerFilledIcon, SuitcasesIcon } from "../../../../components/icons";
import { HoverCard, imageZoom } from "../../../../components/motion";
import { cn } from "../../../../utils/classnames";
import { createTypographyHelper } from "../../../../utils/typography";
import { getVehicleCardImage } from "../../../../utils/optimizedImages";

const PanasonicLogo = "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/public_folder/cms-image/cms-fleet-types/panasonic-automotive-bg-black.png";

const ICON_GOLD = "#DBBE78";
// Both WELL Cabin classes get the Panasonic collaboration footer.
const WELL_CABIN_TYPE_NAMES = ["Toyota Alphard WELL Cabin Luxe", "WELL Cabin GranLuxe"];

/** Black "in collaboration with Panasonic" strip at the bottom of the WELL Cabin card. */
const WellCabinFooter = ({ label, isJapanese }) => (
    // Japanese reads "<logo> との協業", English reads "Collaboration with <logo>",
    // so the logo and the text swap sides.
    <div className={cn(
        "tw:flex tw:items-center tw:justify-center tw:gap-2 tw:min-h-[32px] tw:px-3 tw:py-[7px] tw:bg-black",
        isJapanese ? "tw:flex-row-reverse" : "tw:flex-row"
    )}>
        <span className="tw:text-white tw:text-[8px] tw:font-semibold tw:tracking-[3px] tw:uppercase tw:leading-none">
            {label}
        </span>
        <img
            src={PanasonicLogo}
            alt="Panasonic Automotive"
            className="tw:h-[18px] tw:w-auto tw:object-contain"
        />
    </div>
);

/**
 * One capacity figure: an icon, the text under it, and an optional small note.
 * `html` comes from our own translation files (it contains <b> tags), which is
 * why it is rendered with dangerouslySetInnerHTML.
 */
const VehicleSpec = ({ icon, html, note, textClassName }) => (
    <div className="tw:flex tw:flex-col tw:gap-1">
        {icon}
        <div
            className={cn("tw:font-hanken-grotesk", textClassName)}
            dangerouslySetInnerHTML={{ __html: html }}
        ></div>
        {note && (
            <div className="tw:text-[#8D8D8D] tw:text-[12px] tw:!font-[400] tw:leading-4">
                {note}
            </div>
        )}
    </div>
);

const VehicleCard = ({ v, onClick }) => {
    const { t, i18n } = useTranslation();
    const isJapanese = i18n.language === "jp";
    const getTypographyClass = createTypographyHelper(i18n.language);

    // "Toyota Alphard" -> "Toyota.Alphard" (the name) and "Toyota.Alphard.Tag".
    const nameTranslationKey = v.typename.replace(/ /g, ".");

    // A vehicle can have a second name just for tablet/desktop, with its own
    // line breaks ("\n" in the translation file). Add "<name key>.Desktop" to a
    // locale file to use it, e.g. "Toyota.HiAce.Luxury.Cabin.Desktop" in jp.
    // Vehicles without one show the normal name at every screen size.
    const desktopNameTranslationKey = `${nameTranslationKey}.Desktop`;
    const hasDesktopName = i18n.exists(desktopNameTranslationKey);
    const isWellCabin = WELL_CABIN_TYPE_NAMES.includes(v.typename);

    const specTextClassName = cn(
        getTypographyClass(12),
        getTypographyClass(14, "md")
    );

    return (
        <HoverCard
            lift={-6}
            className="tw:relative tw:h-full tw:flex tw:flex-col tw:border tw:border-[#DADADA] tw:rounded-[5px] tw:cursor-pointer"
            onClick={() => onClick(v)}
        >
            {v.isPartnerVehicle && (
                <div className="header-vehicle-partner tw:py-1 tw:text-center align-item-center">
                    <p className="desc-partner-vehicle">
                        {t("FleetPage.ProvidedByParter")}
                    </p>
                </div>
            )}

            <div className="tw:flex-1 tw:flex tw:flex-col tw:md:flex-row">
                {/* Picture + name.
                    The top padding is smaller than the bottom padding on purpose:
                    the car pictures have empty space above the car, so this makes
                    the space above the car and below the name LOOK equal. */}
                <div className="tw:flex tw:flex-col tw:justify-center tw:gap-4 tw:px-6 tw:pt-2 tw:pb-6 tw:md:flex-none tw:md:w-[292px] tw:md:px-7 tw:md:pt-4 tw:md:pb-8">
                    {/* Every picture gets the same 3:2 frame, so files with a
                        different shape (e.g. the wide Toyota Alphard one) still
                        take the same space and the cards line up. */}
                    <motion.img
                        // A small local copy when we have one: the originals are
                        // up to 13650px wide and stalled scrolling while decoding.
                        src={getVehicleCardImage(v.images.png)}
                        alt={v.typename}
                        loading="lazy"
                        decoding="async"
                        className="tw:w-full tw:max-w-[230px] tw:aspect-[3/2] tw:object-contain tw:mx-auto"
                        variants={imageZoom(1.04)}
                    />
                    <div className="tw:flex tw:flex-col tw:gap-2">
                        <div className="tw:font-hanken-grotesk tw:font-semibold tw:text-[#7C7B7B] tw:text-[8px] tw:tracking-[3px] tw:uppercase">
                            {t(`${nameTranslationKey}.Tag`)}
                        </div>
                        <div className="tw:font-libre-caslon-display tw:text-[#212529] tw:text-[22px] tw:leading-tight">
                            {hasDesktopName ? (
                                <>
                                    <span className="tw:md:hidden">{t(nameTranslationKey)}</span>
                                    {/* whitespace-pre-line turns each "\n" into a real line break. */}
                                    <span className="tw:hidden tw:md:inline tw:whitespace-pre-line">
                                        {t(desktopNameTranslationKey)}
                                    </span>
                                </>
                            ) : (
                                t(nameTranslationKey)
                            )}
                        </div>
                    </div>
                </div>

                {/* Divider between the two blocks. Only needed on mobile, where
                    they are stacked. */}
                <div className="tw:mx-6 tw:border-t tw:border-t-[#F4F4F4] tw:md:hidden"></div>

                {/* Capacity: side by side on mobile, stacked and vertically
                    centred beside the picture from md up. */}
                <div className="tw:grid tw:grid-cols-2 tw:gap-4 tw:px-6 tw:py-5 tw:md:flex-1 tw:md:flex tw:md:flex-col tw:md:justify-center tw:md:gap-6 tw:md:pl-0 tw:md:pr-6 tw:md:py-6">
                    <VehicleSpec
                        icon={<PassengerFilledIcon color={ICON_GOLD} className="tw:w-8 tw:h-auto" aria-label="Passengers" role="img" />}
                        html={t("VehicleType.MaximumPassenger", { maximumPax: v.maxpassenger })}
                        textClassName={specTextClassName}
                    />
                    <VehicleSpec
                        icon={<SuitcasesIcon color={ICON_GOLD} className="tw:w-8 tw:h-auto" aria-label="Suitcases" role="img" />}
                        html={t("VehicleType.MaximumLuggages", { maximumLuggage: v.maxluggages })}
                        note={t("FleetTypePage.suitcaseNote")}
                        textClassName={specTextClassName}
                    />
                </div>
            </div>

            {isWellCabin && (
                <WellCabinFooter
                    label={t("FleetTypePage.ToyotaAlphardWELLCabin.CollaborationWith")}
                    isJapanese={isJapanese}
                />
            )}
        </HoverCard>
    );
};

export default VehicleCard;
